import type { LocalDexieDB } from '~/storage/local-db'
import type { StatePersister } from './types'
import type {
    AsyncQueueItemRow,
    PersistedQueueState,
    QueueItem,
} from '~/features/async-queue/types'

export interface PartitionedDexiePersisterDeps {
    db: LocalDexieDB
    queueId: string
}

export class PartitionedDexiePersister<T>
    implements StatePersister<PersistedQueueState<T>>
{
    constructor(private readonly deps: PartitionedDexiePersisterDeps) {}

    clear: StatePersister<PersistedQueueState<T>>['clear'] = async () => {
        await this.deps.db.transaction(
            'rw',
            [this.deps.db.asyncQueueStates, this.deps.db.asyncQueueItems],
            async () => {
                // 1. Clear items
                await this.deps.db.asyncQueueItems
                    .where({ queueId: this.deps.queueId })
                    .delete()

                // 2. Clear metadata
                await this.deps.db.asyncQueueStates.delete(this.deps.queueId)
            },
        )
    }

    hydrate: StatePersister<PersistedQueueState<T>>['hydrate'] = async () => {
        try {
            // 1. Fetch metadata
            const record = await this.deps.db.asyncQueueStates.get(
                this.deps.queueId,
            )
            if (!record || !record.state) {
                return null
            }
            const partialState = record.state as Omit<
                PersistedQueueState<T>,
                'items'
            >

            // 2. Fetch items
            const itemRows = await this.deps.db.asyncQueueItems
                .where({ queueId: this.deps.queueId })
                .toArray()

            // 3. Reconstruct items
            const items: QueueItem<T>[] = itemRows.map((row) => {
                const item = row.item as QueueItem<T>
                return item
            })
            // Ensure sorted by enqueue time? Array from DB might not be guaranteed order unless indexed query
            // The in-memory queue sorts on retrieval anyway, but good to be safe or just let the queue handle it.
            // We'll just pass them back.

            const fullState: PersistedQueueState<T> = {
                ...partialState,
                items,
            }

            return fullState
        } catch (error) {
            console.warn('Failed to hydrate partitioned queue state:', error)
            return null
        }
    }

    persist: StatePersister<PersistedQueueState<T>>['persist'] = async (
        state,
    ) => {
        const { items, ...metadata } = state

        await this.deps.db.transaction(
            'rw',
            [this.deps.db.asyncQueueStates, this.deps.db.asyncQueueItems],
            async () => {
                // 1. Save metadata
                await this.deps.db.asyncQueueStates.put({
                    queueId: this.deps.queueId,
                    state: metadata,
                    updatedAt: Date.now(),
                })

                // 2. Save items
                // Strategy: Diff items to minimize writes and ensure correct deletions.
                // 1. Identify items to delete (in DB but not in new state).
                // 2. Upsert new/updated items (bulkPut).

                const existingKeys = await this.deps.db.asyncQueueItems
                    .where({ queueId: this.deps.queueId })
                    .primaryKeys()

                const currentItemIds = new Set(items.map((i) => i.id))
                const keysToDelete = existingKeys.filter(
                    ([_, itemId]) => !currentItemIds.has(itemId),
                )

                if (keysToDelete.length > 0) {
                    await this.deps.db.asyncQueueItems.bulkDelete(keysToDelete)
                }

                const rows: AsyncQueueItemRow[] = items.map((i) => ({
                    id: i.id,
                    queueId: this.deps.queueId,
                    item: i,
                    enqueuedAt: i.enqueuedAt,
                }))

                if (rows.length > 0) {
                    await this.deps.db.asyncQueueItems.bulkPut(rows)
                }
            },
        )
    }
}
